import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Send,
  ShieldOff,
  RefreshCw,
  Settings,
  Lock,
  Users,
  Shield,
  DoorClosed,
  LayoutGrid,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  PageHeader,
  Button,
  Card,
  DataTable,
  LoadingState,
  ErrorState,
  Modal,
  ConfirmDialog,
  Input,
  Select,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui'

// ─── API response types ──────────────────────────────────────────────────────

interface AirkeyListResponse<T> {
  data: T[]
  recordCount: number
}

interface AirkeyPerson {
  id: number
  firstName: string
  lastName: string
  email?: string
  phone?: string
}

interface AirkeyMedium {
  id: number
  mediumType: 'SMARTPHONE' | 'CARD' | 'KEYTAG'
  state: 'ACTIVE' | 'INACTIVE' | 'BLOCKED'
  personId?: number
  description?: string
}

interface AirkeyLock {
  id: number
  name: string
  description?: string
  serialNumber?: string
  batteryWarning?: boolean
}

interface AirkeyArea {
  id: number
  name: string
  description?: string
  lockIds?: number[]
}

interface AirkeyLockingSystem {
  id: number
  name: string
  description?: string
}

interface AirkeyAuthorization {
  id: number
  mediumId: number
  lockId?: number
  areaId?: number
  validFrom?: string
  validTo?: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const MEDIUM_TYPE_LABELS: Record<string, string> = {
  SMARTPHONE: 'Smartphone',
  CARD: 'Karte',
  KEYTAG: 'Schlüsselanhänger',
}

function formatDate(iso?: string) {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? String(err)
  )
}

// ─── Send-a-Key Modal ────────────────────────────────────────────────────────

interface SendKeyModalProps {
  open: boolean
  onClose: () => void
  persons: AirkeyPerson[]
  locks: AirkeyLock[]
  preselectedPersonId?: number
}

function SendKeyModal({ open, onClose, persons, locks, preselectedPersonId }: SendKeyModalProps) {
  const qc = useQueryClient()
  const [personId, setPersonId] = useState<string>(
    preselectedPersonId ? String(preselectedPersonId) : '',
  )
  const [mediumId, setMediumId] = useState('')
  const [lockId, setLockId] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)

  const mediaQuery = useQuery<AirkeyListResponse<AirkeyMedium>>({
    queryKey: ['airkey', 'media'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyMedium>>('/airkey/media', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const personMedia = (mediaQuery.data?.data ?? []).filter(
    (m) => m.personId === Number(personId) && m.state === 'ACTIVE',
  )

  const sendMut = useMutation({
    mutationFn: async () => {
      await apiClient.post('/airkey/authorizations', {
        mediumId: Number(mediumId),
        lockId: Number(lockId),
        ...(validFrom ? { validFrom: new Date(validFrom).toISOString() } : {}),
        ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['airkey', 'authorizations'] })
      handleClose()
    },
    onError: (err) => setSendError(extractErrorMessage(err)),
  })

  function handleClose() {
    setPersonId(preselectedPersonId ? String(preselectedPersonId) : '')
    setMediumId('')
    setLockId('')
    setValidFrom('')
    setValidTo('')
    setSendError(null)
    onClose()
  }

  const personOptions = [
    { value: '', label: '– Person auswählen –' },
    ...persons.map((p) => ({ value: String(p.id), label: `${p.firstName} ${p.lastName}` })),
  ]

  const mediumOptions = [
    { value: '', label: personMedia.length === 0 ? 'Keine aktiven Medien' : '– Medium auswählen –' },
    ...personMedia.map((m) => ({
      value: String(m.id),
      label: `${MEDIUM_TYPE_LABELS[m.mediumType] ?? m.mediumType}${m.description ? ` – ${m.description}` : ''}`,
    })),
  ]

  const lockOptions = [
    { value: '', label: '– Schloss auswählen –' },
    ...locks.map((l) => ({ value: String(l.id), label: l.name })),
  ]

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Schlüssel senden"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={() => sendMut.mutate()}
            disabled={!personId || !mediumId || !lockId || sendMut.isPending}
          >
            <Send className="w-4 h-4 mr-1.5" />
            {sendMut.isPending ? 'Wird gesendet…' : 'Schlüssel senden'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="Person"
          value={personId}
          onValueChange={(v) => {
            setPersonId(v)
            setMediumId('')
          }}
          options={personOptions}
        />

        {personId && (
          <Select
            label="Medium (Smartphone / Karte)"
            value={mediumId}
            onValueChange={setMediumId}
            disabled={mediaQuery.isLoading || personMedia.length === 0}
            hint={mediaQuery.isLoading ? 'Laden…' : undefined}
            options={mediumOptions}
          />
        )}

        <Select
          label="Schloss"
          value={lockId}
          onValueChange={setLockId}
          options={lockOptions}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Gültig von (optional)"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
          <Input
            label="Gültig bis (optional)"
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
          />
        </div>

        {sendError && (
          <ErrorState title="Fehler beim Senden" details={sendError} size="sm" />
        )}
      </div>
    </Modal>
  )
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function UebersichtTab() {
  const systemsQuery = useQuery<AirkeyListResponse<AirkeyLockingSystem>>({
    queryKey: ['airkey', 'lockingsystems'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyLockingSystem>>(
        '/airkey/lockingsystems',
        { params: { limit: 100 } },
      )
      return data
    },
  })

  const areasQuery = useQuery<AirkeyListResponse<AirkeyArea>>({
    queryKey: ['airkey', 'areas'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyArea>>('/airkey/areas', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const locksQuery = useQuery<AirkeyListResponse<AirkeyLock>>({
    queryKey: ['airkey', 'locks'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyLock>>('/airkey/locks', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const lockColumns: ColumnDef<AirkeyLock>[] = [
    { accessorKey: 'name', header: 'Bezeichnung' },
    {
      accessorKey: 'serialNumber',
      header: 'Seriennummer',
      cell: ({ row }) => row.original.serialNumber ?? '–',
    },
    {
      accessorKey: 'description',
      header: 'Beschreibung',
      cell: ({ row }) => row.original.description ?? '–',
    },
    {
      id: 'battery',
      header: 'Batterie',
      size: 80,
      cell: ({ row }) =>
        row.original.batteryWarning ? (
          <span className="text-amber-600 text-xs font-medium">⚠ Niedrig</span>
        ) : (
          <span className="text-green-600 text-xs">OK</span>
        ),
    },
  ]

  const areaColumns: ColumnDef<AirkeyArea>[] = [
    { accessorKey: 'name', header: 'Bereich' },
    {
      accessorKey: 'description',
      header: 'Beschreibung',
      cell: ({ row }) => row.original.description ?? '–',
    },
    {
      id: 'lockCount',
      header: 'Schlösser',
      size: 80,
      cell: ({ row }) => row.original.lockIds?.length ?? 0,
    },
  ]

  if (systemsQuery.isError) {
    return (
      <ErrorState
        title="Airkey nicht erreichbar"
        details={extractErrorMessage(systemsQuery.error)}
      />
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-brand-500" />
            <Card.Title>
              Schließanlagen
              {systemsQuery.data && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  ({systemsQuery.data.recordCount})
                </span>
              )}
            </Card.Title>
          </div>
        </Card.Header>
        <Card.Body>
          {systemsQuery.isLoading ? (
            <LoadingState label="Schließanlagen werden geladen…" />
          ) : (
            <div className="space-y-1">
              {(systemsQuery.data?.data ?? []).map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-sm py-1.5">
                  <LayoutGrid className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium">{s.name}</span>
                  {s.description && <span className="text-gray-500">– {s.description}</span>}
                </div>
              ))}
              {(systemsQuery.data?.data ?? []).length === 0 && (
                <p className="text-sm text-gray-500">Keine Schließanlagen gefunden.</p>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <DoorClosed className="w-4 h-4 text-brand-500" />
            <Card.Title>
              Schlösser
              {locksQuery.data && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  ({locksQuery.data.recordCount})
                </span>
              )}
            </Card.Title>
          </div>
        </Card.Header>
        <Card.Body>
          {locksQuery.isLoading ? (
            <LoadingState label="Schlösser werden geladen…" />
          ) : locksQuery.isError ? (
            <ErrorState
              title="Fehler beim Laden"
              details={extractErrorMessage(locksQuery.error)}
              size="sm"
            />
          ) : (
            <DataTable columns={lockColumns} data={locksQuery.data?.data ?? []} />
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-500" />
            <Card.Title>
              Bereiche
              {areasQuery.data && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  ({areasQuery.data.recordCount})
                </span>
              )}
            </Card.Title>
          </div>
        </Card.Header>
        <Card.Body>
          {areasQuery.isLoading ? (
            <LoadingState label="Bereiche werden geladen…" />
          ) : areasQuery.isError ? (
            <ErrorState
              title="Fehler beim Laden"
              details={extractErrorMessage(areasQuery.error)}
              size="sm"
            />
          ) : (
            <DataTable columns={areaColumns} data={areasQuery.data?.data ?? []} />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Persons Tab ──────────────────────────────────────────────────────────────

interface PersonenTabProps {
  locks: AirkeyLock[]
  onSendKey: (personId: number) => void
}

function PersonenTab({ locks, onSendKey }: PersonenTabProps) {
  const personsQuery = useQuery<AirkeyListResponse<AirkeyPerson>>({
    queryKey: ['airkey', 'persons'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyPerson>>('/airkey/persons', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const mediaQuery = useQuery<AirkeyListResponse<AirkeyMedium>>({
    queryKey: ['airkey', 'media'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyMedium>>('/airkey/media', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const authsQuery = useQuery<AirkeyListResponse<AirkeyAuthorization>>({
    queryKey: ['airkey', 'authorizations'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyAuthorization>>(
        '/airkey/authorizations',
        { params: { limit: 500 } },
      )
      return data
    },
  })

  const mediaByPerson = new Map<number, AirkeyMedium[]>()
  for (const m of mediaQuery.data?.data ?? []) {
    if (m.personId != null) {
      const list = mediaByPerson.get(m.personId) ?? []
      list.push(m)
      mediaByPerson.set(m.personId, list)
    }
  }

  const authsByMedium = new Map<number, AirkeyAuthorization[]>()
  for (const a of authsQuery.data?.data ?? []) {
    const list = authsByMedium.get(a.mediumId) ?? []
    list.push(a)
    authsByMedium.set(a.mediumId, list)
  }

  const columns: ColumnDef<AirkeyPerson>[] = [
    {
      accessorKey: 'firstName',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: 'E-Mail',
      cell: ({ row }) => row.original.email ?? '–',
    },
    {
      accessorKey: 'phone',
      header: 'Telefon',
      cell: ({ row }) => row.original.phone ?? '–',
    },
    {
      id: 'media',
      header: 'Medien',
      size: 140,
      cell: ({ row }) => {
        const media = mediaByPerson.get(row.original.id) ?? []
        if (media.length === 0) return <span className="text-gray-400 text-xs">–</span>
        return (
          <div className="flex flex-wrap gap-1">
            {media.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5"
              >
                {MEDIUM_TYPE_LABELS[m.mediumType] ?? m.mediumType}
              </span>
            ))}
          </div>
        )
      },
    },
    {
      id: 'activeAuths',
      header: 'Berechtigungen',
      size: 110,
      cell: ({ row }) => {
        const media = mediaByPerson.get(row.original.id) ?? []
        const count = media.reduce(
          (sum, m) => sum + (authsByMedium.get(m.id)?.length ?? 0),
          0,
        )
        return <span className="text-sm">{count}</span>
      },
    },
    {
      id: 'actions',
      size: 140,
      cell: ({ row }) => (
        <Button
          variant="secondary"
          size="xs"
          onClick={() => onSendKey(row.original.id)}
          disabled={locks.length === 0}
        >
          <Send className="w-3.5 h-3.5 mr-1" />
          Schlüssel senden
        </Button>
      ),
    },
  ]

  if (personsQuery.isError) {
    return (
      <ErrorState
        title="Fehler beim Laden der Personen"
        details={extractErrorMessage(personsQuery.error)}
      />
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-brand-500" />
          <Card.Title>
            Personen
            {personsQuery.data && (
              <span className="ml-2 text-xs text-gray-400 font-normal">
                ({personsQuery.data.recordCount})
              </span>
            )}
          </Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        {personsQuery.isLoading ? (
          <LoadingState label="Personen werden geladen…" />
        ) : (
          <DataTable columns={columns} data={personsQuery.data?.data ?? []} />
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Authorizations Tab ───────────────────────────────────────────────────────

interface BerechtigungenTabProps {
  persons: AirkeyPerson[]
  locks: AirkeyLock[]
  onSendKey: () => void
}

function BerechtigungenTab({ persons, locks, onSendKey }: BerechtigungenTabProps) {
  const qc = useQueryClient()
  const [revokeTarget, setRevokeTarget] = useState<AirkeyAuthorization | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const mediaQuery = useQuery<AirkeyListResponse<AirkeyMedium>>({
    queryKey: ['airkey', 'media'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyMedium>>('/airkey/media', {
        params: { limit: 500 },
      })
      return data
    },
  })

  const authsQuery = useQuery<AirkeyListResponse<AirkeyAuthorization>>({
    queryKey: ['airkey', 'authorizations'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyAuthorization>>(
        '/airkey/authorizations',
        { params: { limit: 500 } },
      )
      return data
    },
  })

  const revokeMut = useMutation({
    mutationFn: async (authId: number) => {
      await apiClient.delete(`/airkey/authorizations/${authId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['airkey', 'authorizations'] })
      setRevokeTarget(null)
      setRevokeError(null)
    },
    onError: (err) => setRevokeError(extractErrorMessage(err)),
  })

  const personMap = new Map(persons.map((p) => [p.id, p]))
  const lockMap = new Map(locks.map((l) => [l.id, l]))
  const mediaMap = new Map((mediaQuery.data?.data ?? []).map((m) => [m.id, m]))

  const columns: ColumnDef<AirkeyAuthorization>[] = [
    {
      id: 'medium',
      header: 'Medium',
      cell: ({ row }) => {
        const medium = mediaMap.get(row.original.mediumId)
        return (
          <span className="text-sm">
            {medium
              ? `${MEDIUM_TYPE_LABELS[medium.mediumType] ?? medium.mediumType}${medium.description ? ` – ${medium.description}` : ''}`
              : `Medium #${row.original.mediumId}`}
          </span>
        )
      },
    },
    {
      id: 'person',
      header: 'Person',
      cell: ({ row }) => {
        const medium = mediaMap.get(row.original.mediumId)
        if (!medium?.personId) return <span className="text-gray-400">–</span>
        const person = personMap.get(medium.personId)
        return person ? (
          <span className="text-sm">
            {person.firstName} {person.lastName}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">–</span>
        )
      },
    },
    {
      id: 'target',
      header: 'Schloss / Bereich',
      cell: ({ row }) => {
        if (row.original.lockId) {
          const lock = lockMap.get(row.original.lockId)
          return (
            <span className="flex items-center gap-1 text-sm">
              <Lock className="w-3.5 h-3.5 text-gray-400" />
              {lock?.name ?? `Schloss #${row.original.lockId}`}
            </span>
          )
        }
        if (row.original.areaId) {
          return <span className="text-sm text-gray-600">Bereich #{row.original.areaId}</span>
        }
        return <span className="text-gray-400">–</span>
      },
    },
    {
      id: 'validity',
      header: 'Gültigkeit',
      cell: ({ row }) => {
        const from = formatDate(row.original.validFrom)
        const to = formatDate(row.original.validTo)
        if (from === '–' && to === '–')
          return <span className="text-sm text-green-600">Unbegrenzt</span>
        return (
          <span className="text-sm">
            {from} – {to}
          </span>
        )
      },
    },
    {
      id: 'actions',
      size: 120,
      cell: ({ row }) => (
        <Button
          variant="danger"
          size="xs"
          onClick={() => {
            setRevokeError(null)
            setRevokeTarget(row.original)
          }}
        >
          <ShieldOff className="w-3.5 h-3.5 mr-1" />
          Entziehen
        </Button>
      ),
    },
  ]

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-500" />
            <Card.Title>
              Berechtigungen
              {authsQuery.data && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  ({authsQuery.data.recordCount})
                </span>
              )}
            </Card.Title>
          </div>
          <Button variant="primary" size="sm" onClick={onSendKey}>
            <Send className="w-4 h-4 mr-1.5" />
            Schlüssel senden
          </Button>
        </Card.Header>
        <Card.Body>
          {authsQuery.isLoading ? (
            <LoadingState label="Berechtigungen werden geladen…" />
          ) : authsQuery.isError ? (
            <ErrorState
              title="Fehler beim Laden"
              details={extractErrorMessage(authsQuery.error)}
              size="sm"
            />
          ) : (
            <DataTable columns={columns} data={authsQuery.data?.data ?? []} />
          )}
        </Card.Body>
      </Card>

      {revokeError && (
        <div className="mt-2">
          <ErrorState title="Berechtigung konnte nicht entzogen werden" details={revokeError} size="sm" />
        </div>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Berechtigung entziehen"
        message={`Soll Berechtigung #${revokeTarget?.id} sofort entzogen werden? Der Zugang wird unverzüglich gesperrt und kann nicht rückgängig gemacht werden.`}
        confirmLabel="Entziehen"
        variant="danger"
        loading={revokeMut.isPending}
        onConfirm={() => {
          if (revokeTarget) revokeMut.mutate(revokeTarget.id)
        }}
        onCancel={() => {
          setRevokeTarget(null)
          setRevokeError(null)
        }}
      />
    </>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

type TabId = 'uebersicht' | 'personen' | 'berechtigungen'

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'uebersicht', label: 'Übersicht', icon: LayoutGrid },
  { id: 'personen', label: 'Personen', icon: Users },
  { id: 'berechtigungen', label: 'Berechtigungen', icon: Shield },
]

export function SchluesselverwaltungPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabId>('uebersicht')
  const [sendKeyOpen, setSendKeyOpen] = useState(false)
  const [sendKeyPersonId, setSendKeyPersonId] = useState<number | undefined>()

  const configQuery = useQuery<{ configured: boolean; baseUrl: string }>({
    queryKey: ['airkey', 'config'],
    queryFn: async () => {
      const { data } = await apiClient.get('/airkey/config')
      return data
    },
    retry: false,
  })

  const personsQuery = useQuery<AirkeyListResponse<AirkeyPerson>>({
    queryKey: ['airkey', 'persons'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyPerson>>('/airkey/persons', {
        params: { limit: 500 },
      })
      return data
    },
    enabled: configQuery.data?.configured === true,
  })

  const locksQuery = useQuery<AirkeyListResponse<AirkeyLock>>({
    queryKey: ['airkey', 'locks'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyLock>>('/airkey/locks', {
        params: { limit: 500 },
      })
      return data
    },
    enabled: configQuery.data?.configured === true,
  })

  if (configQuery.isLoading) {
    return <LoadingState label="Airkey wird initialisiert…" />
  }

  if (!configQuery.data?.configured) {
    return (
      <div className="p-6 max-w-lg mx-auto mt-12">
        <ErrorState
          title="Airkey nicht konfiguriert"
          description="Für diesen Mandanten ist noch kein Airkey API-Key hinterlegt."
          onRetry={() => navigate('/einstellungen/airkey')}
          retryLabel="Zu den Einstellungen"
        />
      </div>
    )
  }

  const persons = personsQuery.data?.data ?? []
  const locks = locksQuery.data?.data ?? []

  function handleSendKey(personId?: number) {
    setSendKeyPersonId(personId)
    setSendKeyOpen(true)
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Schlüsselverwaltung"
        breadcrumbs={[{ label: 'Schlüsselverwaltung' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ['airkey'] })}
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Aktualisieren
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate('/einstellungen/airkey')}>
              <Settings className="w-4 h-4 mr-1.5" />
              Einstellungen
            </Button>
            <Button variant="primary" size="sm" onClick={() => handleSendKey()}>
              <Send className="w-4 h-4 mr-1.5" />
              Schlüssel senden
            </Button>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
        <TabsList>
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="uebersicht"><UebersichtTab /></TabsContent>
        <TabsContent value="personen">
          <PersonenTab locks={locks} onSendKey={(pid) => handleSendKey(pid)} />
        </TabsContent>
        <TabsContent value="berechtigungen">
          <BerechtigungenTab
            persons={persons}
            locks={locks}
            onSendKey={() => handleSendKey()}
          />
        </TabsContent>
      </Tabs>

      <SendKeyModal
        open={sendKeyOpen}
        onClose={() => {
          setSendKeyOpen(false)
          setSendKeyPersonId(undefined)
        }}
        persons={persons}
        locks={locks}
        preselectedPersonId={sendKeyPersonId}
      />
    </div>
  )
}
